/**
 * Auth Context
 *
 * Provides global state management for user authentication using React Context API.
 * User data is kept in memory (not localStorage) for better security.
 * Only tokens are persisted in localStorage.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { getUserProfile } from '../services/userService';

// User data interface
export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role: string;
  isAdmin: boolean; // DEPRECATED: Legacy role-based admin check
  isSuperAdmin: boolean; // NEW: Superadmin flag (can access all workspaces)
  isWorkspaceAdmin: boolean; // NEW: Workspace permission-based admin check
  canManageAgents: boolean; // NEW: Can manage agents, knowledge bases, data sources
  canManageWorkspace: boolean; // NEW: Can manage workspace members and groups
  canViewAnalytics: boolean; // NEW: Can view analytics dashboard
  canAdmin: boolean; // NEW: Full admin privileges (can_admin permission)
}

// Context interface
interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
  clearUser: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider component
 * Wraps the application to provide authentication state
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch user data from backend
   */
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');

    if (!token || token === 'undefined') {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await getUserProfile();
      if (response?.data) {
        const { id, name, email, role, is_superadmin, is_workspace_admin, can_manage_agents, can_manage_workspace, can_view_analytics, can_admin } = response.data;
        setUser({
          id,
          name: name || '',
          email: email || '',
          role: role || 'user',
          isAdmin: role === 'admin' || role === 'superadmin', // DEPRECATED: Legacy check
          isSuperAdmin: is_superadmin || false, // NEW: Superadmin flag (full system access)
          isWorkspaceAdmin: is_workspace_admin || false, // NEW: Workspace permission check
          canManageAgents: can_manage_agents || false, // NEW: Can manage agents
          canManageWorkspace: can_manage_workspace || false, // NEW: Can manage workspace
          canViewAnalytics: can_view_analytics || false, // NEW: Can view analytics
          canAdmin: can_admin || false, // NEW: Full admin privileges
        });
      }
    } catch {
      // Token invalid or expired - clear user
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear user data (called on logout)
   */
  const clearUser = useCallback(() => {
    setUser(null);
  }, []);

  // Load user on mount
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Listen for auth events (login/logout)
  useEffect(() => {
    const handleAuthChange = () => {
      refreshUser();
    };

    // Custom event dispatched after login/logout
    window.addEventListener('authStateChanged', handleAuthChange);

    // Storage event for cross-tab sync
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token') {
        refreshUser();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refreshUser]);

  // Memoize context value
  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      refreshUser,
      clearUser,
    }),
    [user, isLoading, refreshUser, clearUser]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the AuthContext
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
